import {Trans} from '@lingui/macro';
import React, {useState} from 'react';
import styled from 'styled-components';

import {Handler, HandlerOf} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {Button} from '../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {SimpleDropdownWithHeader} from '../../../../libs/shared/ui/src/components/dropdowns/simpleDropdownWithHeader';
import {Icon} from '../../../../libs/shared/ui/src/components/icon/icon';
import {PopoverTransferProps} from '../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {VisualSizesEnum} from '../../../../libs/shared/ui/src/styles/commonStyles';
import {openInBrowser} from '../../../../srcCommon/browser/openInBrowser';
import {InboxesFormatter} from '../../../components/common/formatters/inboxesFormatter';
import {Paragraph} from '../../../components/common/paragraph';
import {InlineBanner, InlineBannerTypesEnum} from '../../../components/inlineBanner/inlineBanner';
import {BusinessHours} from '../../../models/api/businessHoursModel';
import {InboxModel} from '../../../models/api/inboxModel';
import {helpCenterUrl} from '../../../store/stateless/navigationActions';
import {hasAnyBusinessHour} from '../../helpers/businessHoursHelpers';
import {StyledFlex} from '../reportViewer/reportCommons';
import {BusinessHoursProps} from './businessHours';
import {BusinessHoursContent} from './businessHoursContent';
import {BusinessHoursDialogCta} from './businessHoursDialogCta';

/*
  Props.
*/

interface BusinessHoursDialogProps extends BusinessHoursProps {
  onSave: HandlerOf<BusinessHours>;
  onDisable: Handler;
  modalProps: PopoverTransferProps;
  hasUpdateError?: boolean;
}

/*
  Style.
*/

const StyledContentDiv = styled.div`
  padding: 15px;
`;

const StyledInlineBanner = styled(InlineBanner)`
  margin: 0 15px 4px 15px;
`;

const StyledInlineBannerContentDiv = styled.div`
  line-height: 16px;
`;

const StyledParagraph = styled(Paragraph)`
  margin-bottom: 20px;
`;

const StyledIcon = styled(Icon)`
  margin-left: 8px;
`;

const StyledButton = styled(Button)`
  display: inline-block;
  margin-top: 8px;
  margin-left: -12px;
`;

const StyledLearnMoreSpan = styled.span`
  text-transform: capitalize;
`;

/*
  Component.
*/

export const BusinessHoursDialog = (props: BusinessHoursDialogProps) => {
  const {
    calendarWeekStartDay,
    businessHours,
    timeFormat,
    teamName,
    modalProps,
    onSave,
    onDisable,
    teamInboxesWithBusinessHours,
    hasUpdateError,
  } = props;
  const [currentBusinessHours, setCurrentBusinessHours] = useState(businessHours);
  const [displayWarningBanner, setDisplayWarningBanner] = useState(true);
  const [isDirty, setIsDirty] = useState(false);
  const hasBusinessHours = hasAnyBusinessHour(currentBusinessHours);

  const onConfirm = () => {
    onSave(currentBusinessHours);
  };

  const onUpdate = (updatedBusinessHours: BusinessHours) => {
    setCurrentBusinessHours(updatedBusinessHours);
    setIsDirty(true);
  };

  return (
    <SimpleDropdownWithHeader
      title={<Trans id="set-business-hours-title">Set business hours for {teamName}</Trans>}
      interactionId="business-hours-picker"
      width={300}
      hasBackground
      footer={
        <BusinessHoursDialogCta
          onCancel={modalProps.onRequestClose}
          onConfirm={onConfirm}
          confirmDisabled={(!businessHours.enabled && !hasBusinessHours) || !isDirty}
        />
      }
      {...modalProps}
    >
      <StyledContentDiv>
        <StyledParagraph>
          <Trans id="business-hours-dialog-intro">
            Define business hours for this team to better inform time sensitive analytics and rules.
          </Trans>
        </StyledParagraph>
        <BusinessHoursContent
          onUpdate={onUpdate}
          onDisable={onDisable}
          businessHours={currentBusinessHours}
          calendarWeekStartDay={calendarWeekStartDay}
          timeFormat={timeFormat}
          hasUpdateError={hasUpdateError}
        />
      </StyledContentDiv>
      {maybeDisplayWarningMessageAboutInboxesWithBusinessHours(
        teamInboxesWithBusinessHours,
        setDisplayWarningBanner,
        displayWarningBanner,
      )}
    </SimpleDropdownWithHeader>
  );
};

/*
 * Helpers.
 */

// If some inboxes in this team have business hours set up, we warn the user about potential conflicts.
function maybeDisplayWarningMessageAboutInboxesWithBusinessHours(
  teamInboxesWithBusinessHours: ReadonlyArray<InboxModel>,
  setDisplayWarningBanner: HandlerOf<boolean>,
  displayWarningBanner: boolean,
) {
  if (!teamInboxesWithBusinessHours.length || !displayWarningBanner) {
    return null;
  }

  return (
    <StyledInlineBanner
      onClose={() => setDisplayWarningBanner(false)}
      type={InlineBannerTypesEnum.INFO}
      size={VisualSizesEnum.SMALL}
      title={
        <StyledInlineBannerContentDiv>
          <div>
            <Trans id="business-hours-dialog-inbox-warning-message-short">
              Inbox business hours exist for{' '}
              <InboxesFormatter
                inboxes={teamInboxesWithBusinessHours}
                limit={3}
                renderInboxName={(inboxName) => <strong>{inboxName}</strong>}
                renderOthersText={(_othersText, _othersName, othersCount) => (
                  <strong>
                    <Trans id="business-hours-inbox-others-text">{othersCount} other inboxes</Trans>
                  </strong>
                )}
              />
              . Inbox business hours take priority over team business hours.
            </Trans>
          </div>
          <StyledButton
            onClick={() => {
              openInBrowser(`${helpCenterUrl}/t/35hz4tx/business-hours`);
            }}
            size={VisualSizesEnum.SMALL}
            variant={ButtonVariantsEnum.LINK}
          >
            <StyledFlex>
              <StyledLearnMoreSpan>
                <Trans id="business-hours-dialog-inbox-warning-learn-more">Learn more</Trans>
              </StyledLearnMoreSpan>
              <StyledIcon name="externalLinkSmall" />
            </StyledFlex>
          </StyledButton>
        </StyledInlineBannerContentDiv>
      }
    />
  );
}
